/**
 * Leading at Gallo Analytics Dashboard
 * Provides analytics for 360 survey data including individual progress,
 * cohort analytics, and comparison views
 */

import React, { useState, useEffect } from 'react';
import { 
  Users, 
  TrendingUp, 
  TrendingDown,
  Target,
  Download,
  Calendar,
  Filter,
  ChevronDown,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  Award
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { leadingAnalyticsAPI } from '../../services/leadingAnalyticsAPI';
import AdminLayout from './AdminLayout';
import ExportModal from './ExportModal';
import './LeadingAnalyticsDashboard.css';
import './LeadingAnalyticsDashboard-cohort.css';

const LeadingAnalyticsDashboard = () => {
  const [activeView, setActiveView] = useState('overall'); // overall, individual, cohort, comparison
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState('CSV');
  const [showExportModal, setShowExportModal] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: '90d',
    programLevel: 'all'
  });

  // Navigation tabs
  const views = [
    { id: 'overall', label: 'Overall Progress', icon: TrendingUp },
    { id: 'individual', label: 'Individual Progress', icon: Users },
    { id: 'cohort', label: 'Cohort Analytics', icon: BarChart3 },
    { id: 'comparison', label: 'Cohort Comparison', icon: Target }
  ];

  const handleExport = async () => {
    setExportLoading(true);
    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/analytics/export', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     export_type: activeView === 'individual' ? 'INDIVIDUAL_PROGRESS' : 'COHORT_DATA',
      //     format: exportFormat,
      //     filters: filters
      //   })
      // });
      // const data = await response.json();
      
      // Mock export
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert(`Export completed! Format: ${exportFormat}\nIn production, a download link would be provided.`);
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="leading-analytics-container">
        {/* Header */}
        <div className="leading-analytics-header">
          <div>
            <h1 className="leading-analytics-title">Analytics & Reporting</h1>
            <p className="leading-analytics-subtitle">
              Track competency improvement and program effectiveness
            </p>
          </div>
        </div>

        {/* View Navigation */}
        <div className="leading-analytics-nav">
        {views.map(view => {
          const Icon = view.icon;
          return (
            <button
              key={view.id}
              className={`leading-analytics-nav-item ${activeView === view.id ? 'active' : ''}`}
              onClick={() => setActiveView(view.id)}
            >
              <Icon size={20} />
              <span>{view.label}</span>
            </button>
          );
        })}
        </div>

        {/* Filters Bar */}
        <div className="leading-analytics-filters">
          <div className="leading-filter-group">
            <label className="leading-filter-label">
              <Calendar size={16} />
              Date Range
            </label>
            <select
              className="leading-filter-select"
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
            >
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="6m">Last 6 Months</option>
              <option value="1y">Last Year</option>
              <option value="all">All Time</option>
            </select>
          </div>

          <div className="leading-filter-group">
            <label className="leading-filter-label">
              <Filter size={16} />
              Program Level
            </label>
            <select
              className="leading-filter-select"
              value={filters.programLevel}
              onChange={(e) => setFilters({ ...filters, programLevel: e.target.value })}
            >
              <option value="all">All Levels</option>
              <option value="VISIONARY_LEADERS">Visionary Leaders</option>
              <option value="LEADERS_OF_LEADERS">Leaders of Leaders</option>
              <option value="EXPERT_LEADERS">Expert Leaders</option>
              <option value="FRONTLINE_LEADERS">Frontline Leaders</option>
              <option value="INFORMAL_LEADERS">Informal Leaders</option>
            </select>
          </div>
        </div>

        {/* Content Area */}
        <div className="leading-analytics-content">
          {activeView === 'overall' && <OverallProgressView filters={filters} />}
          {activeView === 'individual' && <IndividualProgressView filters={filters} />}
          {activeView === 'cohort' && <CohortAnalyticsView filters={filters} />}
          {activeView === 'comparison' && <CohortComparisonView filters={filters} />}
        </div>

        {/* Export Modal */}
        {showExportModal && (
          <ExportModal
            exportFormat={exportFormat}
            setExportFormat={setExportFormat}
            exportLoading={exportLoading}
            onExport={handleExport}
            onClose={() => setShowExportModal(false)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

/**
 * Individual Progress View Component
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.6, 9.7, 9.8
 */
const IndividualProgressView = ({ filters }) => {
  const { getAccessToken } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Multi-cohort state
  const [selectedCohortView, setSelectedCohortView] = useState('all');
  const [availableCohorts, setAvailableCohorts] = useState([]);
  const [isMultiCohort, setIsMultiCohort] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadParticipants();
  }, [filters, pagination.page, debouncedSearch]);

  useEffect(() => {
    if (selectedParticipant) {
      loadParticipantProgress(selectedParticipant);
    }
  }, [selectedParticipant]);

  useEffect(() => {
    if (selectedParticipant && selectedEmployeeId) {
      loadParticipantProgressForCohort();
    }
  }, [selectedCohortView]);

  const loadParticipants = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leadingAnalyticsAPI.getAllParticipants(
        getAccessToken, 
        filters,
        pagination.page,
        pagination.limit,
        debouncedSearch
      );
      
      setParticipants(data.participants);
      setPagination(data.pagination);
      
      if (data.participants.length > 0 && !selectedParticipant) {
        setSelectedParticipant(data.participants[0].id);
      }
    } catch (error) {
      console.error('Error loading participants:', error);
      setError('Failed to load participants. Please try again.');
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const loadParticipantProgress = async (participantId) => {
    setError(null);
    try {
      // Get the selected participant's employee_id
      const selectedParticipantData = participants.find(p => p.id === participantId);
      
      if (!selectedParticipantData) return;
      
      setSelectedEmployeeId(selectedParticipantData.employee_id);
      
      // Get all cohorts for this employee
      const cohortsData = await leadingAnalyticsAPI.getEmployeeCohorts(
        getAccessToken, 
        selectedParticipantData.employee_id
      );
      
      setAvailableCohorts(cohortsData.cohorts);
      setIsMultiCohort(cohortsData.total_cohorts > 1);
      
      // Reset cohort view to 'all' when selecting new participant
      setSelectedCohortView('all');
      
      // Load data based on cohort count
      if (cohortsData.total_cohorts > 1) {
        // Load multi-cohort aggregated data
        const data = await leadingAnalyticsAPI.getParticipantMultiCohortProgress(
          getAccessToken,
          selectedParticipantData.employee_id
        );
        setProgressData(data);
      } else {
        // Load single cohort data
        const data = await leadingAnalyticsAPI.getParticipantProgress(
          getAccessToken,
          participantId
        );
        setProgressData(data);
      }
    } catch (error) {
      console.error('Error loading participant progress:', error);
      setError('Failed to load participant progress. Please try again.');
      setProgressData(null);
    }
  };

  const loadParticipantProgressForCohort = async () => {
    setError(null);
    setProgressData(null); // Clear data while loading
    
    try {
      if (selectedCohortView === 'all' && isMultiCohort) {
        // Load aggregated multi-cohort data
        console.log('Loading multi-cohort data for employee:', selectedEmployeeId);
        const data = await leadingAnalyticsAPI.getParticipantMultiCohortProgress(
          getAccessToken,
          selectedEmployeeId
        );
        console.log('Multi-cohort data loaded:', data);
        setProgressData(data);
      } else {
        // Load single cohort data
        const cohortParticipantId = selectedCohortView === 'all' 
          ? selectedParticipant 
          : availableCohorts.find(c => c.cohort_id === parseInt(selectedCohortView))?.participant_id;
        
        console.log('Loading single cohort data for participant:', cohortParticipantId);
        
        if (cohortParticipantId) {
          const data = await leadingAnalyticsAPI.getParticipantProgress(
            getAccessToken,
            cohortParticipantId
          );
          console.log('Single cohort data loaded:', data);
          setProgressData(data);
        } else {
          console.error('No participant ID found for cohort:', selectedCohortView);
          setError('Could not find participant data for selected cohort.');
        }
      }
    } catch (error) {
      console.error('Error loading cohort progress:', error);
      setError('Failed to load cohort progress. Please try again.');
      setProgressData(null);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <div className="individual-progress-view">
      {/* Participant Selector */}
      <div className="participant-selector-section">
        <h3 className="section-title">Select Participant</h3>
        <input
          type="text"
          className="leading-search-input"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="participant-list">
          {loading ? (
            <div className="loading-state-small">
              <div className="spinner-small"></div>
              <p>Loading participants...</p>
            </div>
          ) : participants.length > 0 ? (
            participants.map(participant => (
              <button
                key={participant.id}
                className={`participant-item ${selectedParticipant === participant.id ? 'active' : ''}`}
                onClick={() => setSelectedParticipant(participant.id)}
              >
                <div className="participant-avatar">
                  {participant.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="participant-info">
                  <div className="participant-name">{participant.name}</div>
                  <div className="participant-meta">{participant.email}</div>
                </div>
                {selectedParticipant === participant.id && (
                  <CheckCircle size={18} className="participant-selected-icon" />
                )}
              </button>
            ))
          ) : (
            <div className="empty-state-small">
              <p>No participants found</p>
            </div>
          )}
        </div>
        
        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={!pagination.hasPrev}
            >
              Previous
            </button>
            
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages}
              <span className="pagination-total"> ({pagination.total} total)</span>
            </span>
            
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={!pagination.hasNext}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Progress Details */}
      {progressData ? (
        <div className="progress-details-section">
          {/* Cohort Selector (only show if multiple cohorts) */}
          {isMultiCohort && (
            <div className="cohort-view-selector">
              <label>View Data For:</label>
              <select
                value={selectedCohortView}
                onChange={(e) => setSelectedCohortView(e.target.value)}
                className="cohort-view-select"
              >
                <option value="all">All Cohorts (Aggregated)</option>
                {availableCohorts.map(cohort => (
                  <option key={cohort.cohort_id} value={cohort.cohort_id}>
                    {cohort.cohort_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Participant Info Card */}
          {progressData.participant && (
            <ParticipantInfoCard 
              participant={progressData.participant}
              cohorts={availableCohorts}
              isMultiCohort={isMultiCohort}
            />
          )}
          
          {/* Render based on view type */}
          {selectedCohortView === 'all' && isMultiCohort ? (
            <AggregatedProgressView data={progressData} />
          ) : (
            <SingleCohortProgressView data={progressData} />
          )}
        </div>
      ) : selectedParticipant ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading progress data...</p>
        </div>
      ) : (
        <div className="empty-state">
          <Users size={48} />
          <p>Select a participant to view their progress</p>
        </div>
      )}
    </div>
  );
};

/**
 * Aggregated Progress View (Multi-Cohort)
 * Shows only summary statistics across all cohorts
 */
const AggregatedProgressView = ({ data }) => {
  // Safety check - ensure we have the right data structure
  if (!data || !data.aggregated_metrics) {
    return (
      <div className="leading-card">
        <div className="card-content">
          <p>Loading aggregated data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Aggregated Summary Card */}
      <div className="leading-card aggregated-summary-card">
        <div className="card-header">
          <h3 className="card-title">Summary Across All Cohorts</h3>
        </div>
        <div className="card-content">
          <div className="aggregated-stats">
            <div className="stat-item">
              <div className="stat-value">{data.total_cohorts || 0}</div>
              <div className="stat-label">Total Cohorts</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{data.aggregated_metrics.total_surveys_completed || 0}</div>
              <div className="stat-label">Surveys Completed</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {data.aggregated_metrics.average_improvement 
                  ? `${data.aggregated_metrics.average_improvement.toFixed(1)}%`
                  : 'N/A'}
              </div>
              <div className="stat-label">Average Improvement</div>
            </div>
          </div>
          
          {/* Instruction to select specific cohort */}
          <div className="aggregated-instruction">
            <p>Select a specific cohort from the dropdown above to view detailed analytics.</p>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Single Cohort Progress View
 */
const SingleCohortProgressView = ({ data }) => {
  // Handle both single cohort API response and multi-cohort data structure
  const surveyStatus = data.survey_status || {};
  const overallImprovement = data.overall_improvement || {};
  const goalMet = data.goal_met || {};
  const competencyScores = data.competency_scores || {};
  const improvements = data.improvements || {};
  
  return (
    <>
      {Object.keys(surveyStatus).length > 0 && (
        <SurveyCompletionTimeline surveyStatus={surveyStatus} />
      )}
      {Object.keys(overallImprovement).length > 0 && (
        <OverallImprovementCard 
          overallImprovement={overallImprovement}
          goalMet={goalMet}
        />
      )}
      {Object.keys(competencyScores).length > 0 && (
        <CompetencyScoreChart 
          competencyScores={competencyScores}
          improvements={improvements}
        />
      )}
    </>
  );
};

/**
 * Participant Info Card
 */
const ParticipantInfoCard = ({ participant, cohorts = [], isMultiCohort = false }) => {
  return (
    <div className="leading-card participant-info-card">
      <div className="card-header">
        <h3 className="card-title">Participant Information</h3>
      </div>
      <div className="card-content">
        <div className="info-grid">
          <div className="info-item">
            <label>Name</label>
            <value>{participant.name}</value>
          </div>
          <div className="info-item">
            <label>Employee ID</label>
            <value>{participant.employee_id}</value>
          </div>
          <div className="info-item">
            <label>Email</label>
            <value>{participant.email}</value>
          </div>
          <div className="info-item">
            <label>Status</label>
            <value>
              <span className={`status-badge status-${participant.status.toLowerCase()}`}>
                {participant.status}
              </span>
            </value>
          </div>
          
          {/* Show all cohorts if multiple */}
          {isMultiCohort ? (
            <div className="info-item info-item-full-width">
              <label>Enrolled Cohorts ({cohorts.length})</label>
              <value>
                <div className="cohort-list">
                  {cohorts.map(cohort => (
                    <div key={cohort.cohort_id} className="cohort-badge">
                      <span className="cohort-badge-name">{cohort.cohort_name}</span>
                      <span className="cohort-dates">
                        {new Date(cohort.start_date).toLocaleDateString()} - 
                        {new Date(cohort.end_date).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </value>
            </div>
          ) : participant.cohort ? (
            <>
              <div className="info-item">
                <label>Cohort</label>
                <value>{participant.cohort.name}</value>
              </div>
              <div className="info-item">
                <label>Program Level</label>
                <value>{participant.cohort.program_level.replace(/_/g, ' ')}</value>
              </div>
              <div className="info-item">
                <label>Enrollment Date</label>
                <value>{new Date(participant.enrolled_at).toLocaleDateString()}</value>
              </div>
              <div className="info-item">
                <label>Program Duration</label>
                <value>
                  {new Date(participant.cohort.start_date).toLocaleDateString()} - {new Date(participant.cohort.end_date).toLocaleDateString()}
                </value>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/**
 * Survey Completion Timeline
 */
const SurveyCompletionTimeline = ({ surveyStatus }) => {
  const surveys = [
    { type: 'BASELINE', label: 'Baseline Survey', color: '#3b82f6' },
    { type: 'MIDPOINT', label: 'Midpoint Survey', color: '#8b5cf6' },
    { type: 'FINAL', label: 'Final Survey', color: '#10b981' }
  ];

  return (
    <div className="leading-card timeline-card">
      <div className="card-header">
        <h3 className="card-title">Survey Completion Timeline</h3>
      </div>
      <div className="card-content">
        <div className="timeline">
          {surveys.map((survey, index) => {
            const status = surveyStatus[survey.type];
            return (
              <div key={survey.type} className="timeline-item">
                <div className={`timeline-marker ${status.completed ? 'completed' : 'pending'}`}>
                  {status.completed ? (
                    <CheckCircle size={24} style={{ color: survey.color }} />
                  ) : (
                    <Clock size={24} style={{ color: '#9ca3af' }} />
                  )}
                </div>
                <div className="timeline-content">
                  <div className="timeline-label">{survey.label}</div>
                  {status.completed ? (
                    <div className="timeline-meta">
                      <span className="timeline-date">
                        Completed: {new Date(status.completion_date).toLocaleDateString()}
                      </span>
                      <span className="timeline-responses">
                        {status.response_count} responses
                      </span>
                    </div>
                  ) : (
                    <div className="timeline-meta">
                      <span className="timeline-pending">Pending</span>
                    </div>
                  )}
                </div>
                {index < surveys.length - 1 && (
                  <div className={`timeline-connector ${status.completed ? 'completed' : 'pending'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Overall Improvement Card
 */
const OverallImprovementCard = ({ overallImprovement, goalMet }) => {
  const improvement = overallImprovement.average_improvement;
  const isPositive = improvement >= 10;

  return (
    <div className="leading-card improvement-card">
      <div className="card-header">
        <h3 className="card-title">Overall Improvement</h3>
        {goalMet.overall_goal_met ? (
          <span className="goal-badge goal-met">
            <Award size={16} />
            Goal Met
          </span>
        ) : (
          <span className="goal-badge goal-in-progress">
            <Clock size={16} />
            In Progress
          </span>
        )}
      </div>
      <div className="card-content">
        <div className="improvement-summary">
          <div className="improvement-main">
            <div className={`improvement-value ${isPositive ? 'positive' : 'neutral'}`}>
              {improvement !== null ? (
                <>
                  {isPositive ? <TrendingUp size={32} /> : <TrendingDown size={32} />}
                  <span className="improvement-percentage">{improvement.toFixed(2)}%</span>
                </>
              ) : (
                <span className="improvement-pending">Pending</span>
              )}
            </div>
            <div className="improvement-label">Average Improvement</div>
          </div>
          
          <div className="improvement-stats">
            <div className="stat-item">
              <div className="stat-value">{goalMet.competencies_meeting_goal}</div>
              <div className="stat-label">Competencies Meeting Goal</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{goalMet.total_competencies}</div>
              <div className="stat-label">Total Competencies</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{goalMet.percentage_meeting_goal !== null ? goalMet.percentage_meeting_goal.toFixed(0) : '0'}%</div>
              <div className="stat-label">Success Rate</div>
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="improvement-progress">
          <div className="progress-bar-container">
            <div 
              className={`progress-bar-fill ${isPositive ? 'positive' : 'neutral'}`}
              style={{ width: `${Math.min(improvement, 100)}%` }}
            />
            <div className="progress-bar-goal" style={{ left: '10%' }}>
              <div className="progress-bar-goal-line" />
              <div className="progress-bar-goal-label">10% Goal</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Competency Score Chart with Respondent Type Breakdown
 */
const CompetencyScoreChart = ({ competencyScores, improvements }) => {
  const [expandedCompetency, setExpandedCompetency] = useState(null);
  const competencies = Object.keys(improvements);

  const toggleCompetency = (competency) => {
    setExpandedCompetency(expandedCompetency === competency ? null : competency);
  };

  return (
    <div className="leading-card competency-chart-card">
      <div className="card-header">
        <h3 className="card-title">Competency Scores & Improvement</h3>
        <div className="chart-legend">
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#3b82f6' }}></span>
            Baseline
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#8b5cf6' }}></span>
            Midpoint
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#10b981' }}></span>
            Final
          </span>
        </div>
      </div>
      <div className="card-content">
        <div className="competency-chart">
          {competencies.map(competency => {
            const improvement = improvements[competency];
            const baseline = competencyScores.BASELINE[competency];
            const midpoint = competencyScores.MIDPOINT[competency];
            const final = competencyScores.FINAL[competency];
            const isExpanded = expandedCompetency === competency;

            return (
              <div key={competency} className="competency-row-container">
                <div className="competency-row" onClick={() => toggleCompetency(competency)}>
                  <div className="competency-label">
                    {competency}
                    <button className="expand-btn">
                      {isExpanded ? '▼' : '▶'}
                    </button>
                  </div>
                  <div className="competency-bars">
                    {baseline && (
                      <div className="score-bar baseline" style={{ width: `${(baseline.average / 5) * 100}%` }}>
                        <span className="score-value">{baseline.average}</span>
                      </div>
                    )}
                    {midpoint && (
                      <div className="score-bar midpoint" style={{ width: `${(midpoint.average / 5) * 100}%` }}>
                        <span className="score-value">{midpoint.average}</span>
                      </div>
                    )}
                    {final && (
                      <div className="score-bar final" style={{ width: `${(final.average / 5) * 100}%` }}>
                        <span className="score-value">{final.average}</span>
                      </div>
                    )}
                  </div>
                  <div className="competency-improvement">
                    {improvement.improvement_percentage !== null ? (
                      <span className={`improvement-badge ${improvement.goal_met ? 'positive' : 'neutral'}`}>
                        {improvement.goal_met ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {improvement.improvement_percentage.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="improvement-badge pending">Pending</span>
                    )}
                  </div>
                </div>
                
                {/* Respondent Type Breakdown */}
                {isExpanded && (
                  <RespondentTypeBreakdown 
                    competency={competency}
                    baseline={baseline}
                    midpoint={midpoint}
                    final={final}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Respondent Type Breakdown Component
 */
const RespondentTypeBreakdown = ({ competency, baseline, midpoint, final }) => {
  const respondentTypes = [
    { key: 'SELF', label: 'Self Assessment', icon: '👤' },
    { key: 'MANAGER', label: 'Manager', icon: '👔' },
    { key: 'DIRECT_REPORT', label: 'Direct Reports', icon: '👥' },
    { key: 'ADHOC', label: 'Adhoc Feedback', icon: '💬' }
  ];

  const getRespondentData = (surveyData, respondentKey) => {
    if (!surveyData || !surveyData.by_respondent_type) return null;
    const data = surveyData.by_respondent_type[respondentKey];
    if (!data || data.count === 0) return null;
    return data;
  };

  return (
    <div className="respondent-breakdown">
      <div className="breakdown-header">
        <h4>Breakdown by Respondent Type</h4>
      </div>
      <div className="breakdown-content">
        {respondentTypes.map(({ key, label, icon }) => {
          const baselineData = getRespondentData(baseline, key);
          const midpointData = getRespondentData(midpoint, key);
          const finalData = getRespondentData(final, key);
          
          // Skip if no data for this respondent type
          if (!baselineData && !midpointData && !finalData) return null;

          return (
            <div key={key} className="respondent-type-row">
              <div className="respondent-type-label">
                <span className="respondent-icon">{icon}</span>
                <span className="respondent-name">{label}</span>
                {baselineData && <span className="respondent-count">({baselineData.count})</span>}
              </div>
              <div className="respondent-scores">
                {baselineData && (
                  <div className="respondent-score baseline">
                    <span className="score-label">Baseline:</span>
                    <span className="score-value">{baselineData.average}</span>
                  </div>
                )}
                {midpointData && (
                  <div className="respondent-score midpoint">
                    <span className="score-label">Midpoint:</span>
                    <span className="score-value">{midpointData.average}</span>
                  </div>
                )}
                {finalData && (
                  <div className="respondent-score final">
                    <span className="score-label">Final:</span>
                    <span className="score-value">{finalData.average}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Cohort Analytics View Component
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 10.7
 */
const CohortAnalyticsView = ({ filters }) => {
  const { getAccessToken } = useAuth();
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCohorts();
  }, [filters]);

  useEffect(() => {
    if (selectedCohort) {
      loadCohortAnalytics(selectedCohort);
    }
  }, [selectedCohort]);

  const loadCohorts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leadingAnalyticsAPI.getCohorts(getAccessToken, filters);
      const cohortsList = data.cohorts || [];
      
      setCohorts(cohortsList);
      if (cohortsList.length > 0 && !selectedCohort) {
        setSelectedCohort(cohortsList[0].id);
      }
    } catch (error) {
      console.error('Error loading cohorts:', error);
      setError('Failed to load cohorts. Please try again.');
      setCohorts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCohortAnalytics = async (cohortId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await leadingAnalyticsAPI.getCohortAnalytics(getAccessToken, cohortId);
      setAnalyticsData(data);
    } catch (error) {
      console.error('Error loading cohort analytics:', error);
      setError('Failed to load cohort analytics. Please try again.');
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cohort-analytics-view">
      {/* Cohort Selector */}
      <div className="cohort-selector">
        <label className="leading-filter-label">Select Cohort</label>
        <select
          className="leading-filter-select"
          value={selectedCohort || ''}
          onChange={(e) => setSelectedCohort(parseInt(e.target.value))}
        >
          {cohorts.map(cohort => (
            <option key={cohort.id} value={cohort.id}>
              {cohort.name} ({cohort.participant_count} participants)
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading cohort analytics...</p>
        </div>
      ) : analyticsData ? (
        <div className="cohort-analytics-content">
          {/* Cohort Overview Card */}
          <CohortOverviewCard cohort={analyticsData.cohort} statistics={analyticsData.statistics} />
          
          {/* Key Metrics */}
          <div className="cohort-metrics-grid">
            <MetricCard
              title="Average Improvement"
              value={analyticsData.average_improvement !== null ? `${analyticsData.average_improvement.toFixed(1)}%` : 'N/A'}
              icon={TrendingUp}
              color="success"
              subtitle={analyticsData.average_improvement !== null && analyticsData.average_improvement >= 10 ? 'Goal Achieved' : 'Below Goal'}
            />
            <MetricCard
              title="Participants Meeting Goal"
              value={analyticsData.participants_meeting_goal}
              icon={Award}
              color="primary"
              subtitle={analyticsData.success_rate !== null ? `${analyticsData.success_rate.toFixed(1)}% success rate` : 'N/A'}
            />
            <MetricCard
              title="Completion Rate"
              value={analyticsData.statistics.completion_rate !== null ? `${analyticsData.statistics.completion_rate.toFixed(1)}%` : 'N/A'}
              icon={CheckCircle}
              color="info"
              subtitle={`${analyticsData.statistics.completed_surveys}/${analyticsData.statistics.total_survey_instances} surveys`}
            />
            <MetricCard
              title="Total Responses"
              value={analyticsData.statistics.total_responses}
              icon={Users}
              color="accent"
              subtitle={`${analyticsData.statistics.active_participants} active participants`}
            />
          </div>
          
          {/* Competency Averages Chart (now expandable with respondent breakdown) */}
          <CompetencyAveragesChart competencyAverages={analyticsData.competency_averages} />
          
          {/* Participant Completion Tracker */}
          {analyticsData.participant_completion && (
            <ParticipantCompletionTracker participantCompletionData={analyticsData.participant_completion} />
          )}
          
          {/* Competency Heatmap */}
          <CompetencyHeatmap heatmapData={analyticsData.competency_heatmap} />
        </div>
      ) : (
        <div className="empty-state">
          <BarChart3 size={48} />
          <p>Select a cohort to view analytics</p>
        </div>
      )}
    </div>
  );
};

/**
 * Cohort Overview Card
 */
const CohortOverviewCard = ({ cohort, statistics }) => {
  return (
    <div className="leading-card cohort-overview-card">
      <div className="card-header">
        <h3 className="card-title">Cohort Overview</h3>
        <span className={`status-badge status-${cohort.status.toLowerCase()}`}>
          {cohort.status}
        </span>
      </div>
      <div className="card-content">
        <div className="cohort-overview-grid">
          <div className="overview-section">
            <h4 className="overview-section-title">Cohort Details</h4>
            <div className="overview-items">
              <div className="overview-item">
                <label>Cohort Name</label>
                <value>{cohort.name}</value>
              </div>
              <div className="overview-item">
                <label>Program Level</label>
                <value>{cohort.program_level.replace(/_/g, ' ')}</value>
              </div>
              <div className="overview-item">
                <label>Duration</label>
                <value>
                  {new Date(cohort.start_date).toLocaleDateString()} - {new Date(cohort.end_date).toLocaleDateString()}
                </value>
              </div>
            </div>
          </div>
          
          <div className="overview-section">
            <h4 className="overview-section-title">Participation Statistics</h4>
            <div className="overview-items">
              <div className="overview-item">
                <label>Total Participants</label>
                <value>{statistics.total_participants}</value>
              </div>
              <div className="overview-item">
                <label>Active Participants</label>
                <value>{statistics.active_participants}</value>
              </div>
              <div className="overview-item">
                <label>Completion Rate</label>
                <value>{statistics.completion_rate !== null ? statistics.completion_rate.toFixed(1) : '0'}%</value>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Metric Card Component
 */
const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colorMap = {
    success: { bg: '#d1fae5', text: '#065f46' },
    primary: { bg: '#dbeafe', text: '#1e40af' },
    info: { bg: '#e0e7ff', text: '#3730a3' },
    accent: { bg: '#fce7f3', text: '#9f1239' }
  };

  const colors = colorMap[color] || colorMap.primary;

  return (
    <div className="leading-card metric-card">
      <div className="metric-card-content">
        <div className="metric-icon" style={{ background: colors.bg, color: colors.text }}>
          <Icon size={24} />
        </div>
        <div className="metric-details">
          <div className="metric-value">{value}</div>
          <div className="metric-title">{title}</div>
          {subtitle && <div className="metric-subtitle">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
};

/**
 * Competency Averages Chart (Cohort View)
 * Now expandable to show respondent type breakdown
 */
const CompetencyAveragesChart = ({ competencyAverages }) => {
  const [expandedCompetency, setExpandedCompetency] = useState(null);
  const competencies = Object.values(competencyAverages);

  const toggleCompetency = (competency) => {
    setExpandedCompetency(expandedCompetency === competency ? null : competency);
  };

  return (
    <div className="leading-card competency-averages-card">
      <div className="card-header">
        <h3 className="card-title">Competency Performance</h3>
        <div className="chart-legend">
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#3b82f6' }}></span>
            Baseline
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#8b5cf6' }}></span>
            Midpoint
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#10b981' }}></span>
            Final
          </span>
          <span className="legend-item">
            <span className="legend-color" style={{ background: '#f59e0b' }}></span>
            Improvement %
          </span>
        </div>
      </div>
      <div className="card-content">
        <div className="competency-averages-chart">
          {competencies.map(comp => {
            const isExpanded = expandedCompetency === comp.competency;
            const hasRespondentData = comp.by_respondent_type && 
              Object.values(comp.by_respondent_type).some(rt => rt.count_baseline > 0 || rt.count_final > 0);

            return (
              <div key={comp.competency} className="competency-row-container">
                <div className="competency-average-row" onClick={() => hasRespondentData && toggleCompetency(comp.competency)}>
                  <div className="competency-average-label">
                    <div className="competency-name">
                      {comp.competency}
                      {hasRespondentData && (
                        <button className="expand-btn">
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      )}
                    </div>
                    <div className="competency-count">{comp.participant_count} participants</div>
                  </div>
                  <div className="competency-average-bars">
                    <div className="average-bar-group">
                      {comp.average_baseline !== null && comp.average_baseline !== undefined && (
                        <div className="average-bar baseline" style={{ width: `${(comp.average_baseline / 5) * 100}%` }}>
                          <span className="bar-label">{comp.average_baseline.toFixed(2)}</span>
                        </div>
                      )}
                      {comp.average_midpoint !== null && comp.average_midpoint !== undefined && (
                        <div className="average-bar midpoint" style={{ width: `${(comp.average_midpoint / 5) * 100}%` }}>
                          <span className="bar-label">{comp.average_midpoint.toFixed(2)}</span>
                        </div>
                      )}
                      {comp.average_final !== null && comp.average_final !== undefined && (
                        <div className="average-bar final" style={{ width: `${(comp.average_final / 5) * 100}%` }}>
                          <span className="bar-label">{comp.average_final.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="competency-average-improvement">
                    <span className={`improvement-badge ${comp.average_improvement >= 10 ? 'positive' : 'neutral'}`}>
                      {comp.average_improvement >= 10 ? <CheckCircle size={14} /> : <Clock size={14} />}
                      {comp.average_improvement ? comp.average_improvement.toFixed(1) : '0'}%
                    </span>
                  </div>
                </div>
                
                {/* Respondent Type Breakdown for Cohort */}
                {isExpanded && hasRespondentData && (
                  <CohortRespondentTypeBreakdown 
                    competency={comp.competency}
                    respondentTypeData={comp.by_respondent_type}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Competency Heatmap
 */
const CompetencyHeatmap = ({ heatmapData }) => {
  const competencies = Object.values(heatmapData);
  
  // Calculate color intensity based on change percentage
  const getHeatmapColor = (change) => {
    if (change >= 15) return '#10b981'; // Strong green
    if (change >= 10) return '#34d399'; // Medium green
    if (change >= 5) return '#fbbf24'; // Yellow
    return '#f87171'; // Red
  };

  return (
    <div className="leading-card heatmap-card">
      <div className="card-header">
        <h3 className="card-title">Competency Heatmap</h3>
        <div className="heatmap-legend">
          <span className="heatmap-legend-item">
            <span className="heatmap-color" style={{ background: '#f87171' }}></span>
            &lt; 5%
          </span>
          <span className="heatmap-legend-item">
            <span className="heatmap-color" style={{ background: '#fbbf24' }}></span>
            5-10%
          </span>
          <span className="heatmap-legend-item">
            <span className="heatmap-color" style={{ background: '#34d399' }}></span>
            10-15%
          </span>
          <span className="heatmap-legend-item">
            <span className="heatmap-color" style={{ background: '#10b981' }}></span>
            &gt; 15%
          </span>
        </div>
      </div>
      <div className="card-content">
        <div className="heatmap-grid">
          {competencies.map(comp => (
            <div 
              key={comp.competency} 
              className="heatmap-cell"
              style={{ background: getHeatmapColor(comp.change) }}
            >
              <div className="heatmap-cell-title">{comp.competency}</div>
              <div className="heatmap-cell-values">
                <div className="heatmap-value">
                  <span className="heatmap-value-label">Baseline:</span>
                  <span className="heatmap-value-number">{comp.baseline ? comp.baseline.toFixed(2) : 'N/A'}</span>
                </div>
                {comp.midpoint !== null && comp.midpoint !== undefined && (
                  <div className="heatmap-value">
                    <span className="heatmap-value-label">Midpoint:</span>
                    <span className="heatmap-value-number">{comp.midpoint.toFixed(2)}</span>
                  </div>
                )}
                <div className="heatmap-value">
                  <span className="heatmap-value-label">Final:</span>
                  <span className="heatmap-value-number">{comp.final ? comp.final.toFixed(2) : 'N/A'}</span>
                </div>
                <div className="heatmap-change">
                  <TrendingUp size={16} />
                  {comp.change ? comp.change.toFixed(1) : '0'}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Cohort Respondent Type Breakdown Component
 * Shows aggregated respondent type data for cohort analytics
 */
const CohortRespondentTypeBreakdown = ({ competency, respondentTypeData }) => {
  const respondentTypes = [
    { key: 'SELF', label: 'Self Assessment', icon: '👤' },
    { key: 'MANAGER', label: 'Manager', icon: '👔' },
    { key: 'DIRECT_REPORT', label: 'Direct Reports', icon: '👥' },
    { key: 'ADHOC', label: 'Adhoc Feedback', icon: '💬' }
  ];

  return (
    <div className="respondent-breakdown">
      <div className="breakdown-header">
        <h4>Breakdown by Respondent Type</h4>
      </div>
      <div className="breakdown-content">
        {respondentTypes.map(({ key, label, icon }) => {
          const data = respondentTypeData[key];
          
          // Skip if no data for this respondent type
          if (!data || (data.count_baseline === 0 && (data.count_midpoint || 0) === 0 && data.count_final === 0)) return null;

          return (
            <div key={key} className="respondent-type-row">
              <div className="respondent-type-label">
                <span className="respondent-icon">{icon}</span>
                <span className="respondent-name">{label}</span>
                <span className="respondent-count">
                  ({Math.max(data.count_baseline, data.count_midpoint || 0, data.count_final)} responses)
                </span>
              </div>
              <div className="respondent-scores">
                {data.average_baseline !== null && (
                  <div className="respondent-score baseline">
                    <span className="score-label">Baseline:</span>
                    <span className="score-value">{data.average_baseline}</span>
                  </div>
                )}
                {data.average_midpoint !== null && (
                  <div className="respondent-score midpoint">
                    <span className="score-label">Midpoint:</span>
                    <span className="score-value">{data.average_midpoint}</span>
                  </div>
                )}
                {data.average_final !== null && (
                  <div className="respondent-score final">
                    <span className="score-label">Final:</span>
                    <span className="score-value">{data.average_final}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Participant Completion Tracker Component
 * Shows each participant and their survey completion status
 */
const ParticipantCompletionTracker = ({ participantCompletionData }) => {
  const [expandedParticipant, setExpandedParticipant] = useState(null);

  const toggleExpanded = (participantId) => {
    setExpandedParticipant(expandedParticipant === participantId ? null : participantId);
  };

  if (!participantCompletionData || participantCompletionData.length === 0) {
    return null;
  }

  return (
    <div className="leading-card participant-completion-card">
      <div className="card-header">
        <h3 className="card-title">Participant Survey Completion</h3>
        <p className="card-subtitle">Click on completion fraction to see detailed breakdown</p>
      </div>
      <div className="card-content">
        <div className="participant-completion-list">
          {participantCompletionData.map(participant => {
            const isExpanded = expandedParticipant === participant.id;
            
            return (
              <div key={participant.id} className="participant-completion-item">
                <div className="participant-completion-row">
                  <div className="participant-info">
                    <div className="participant-name">{participant.name}</div>
                    <div className="participant-email">{participant.email}</div>
                  </div>
                  
                  {/* Clickable completion fraction */}
                  <button 
                    className="completion-fraction-btn"
                    onClick={() => toggleExpanded(participant.id)}
                    title="Click to see detailed breakdown"
                  >
                    <span className="completion-numerator">{participant.completed_responses}</span>
                    <span className="completion-separator">/</span>
                    <span className="completion-denominator">{participant.expected_responses}</span>
                    <span className="completion-percentage">
                      ({participant.completion_percentage}%)
                    </span>
                    <span className="expand-indicator">{isExpanded ? '▼' : '▶'}</span>
                  </button>
                </div>
                
                {/* Expanded breakdown */}
                {isExpanded && (
                  <RecipientCompletionBreakdown 
                    surveyInstances={participant.survey_instances}
                    participantName={participant.name}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Recipient Completion Breakdown Component
 * Shows detailed breakdown of who has/hasn't completed surveys
 */
const RecipientCompletionBreakdown = ({ surveyInstances, participantName }) => {
  if (!surveyInstances || surveyInstances.length === 0) {
    return (
      <div className="recipient-breakdown">
        <p className="no-surveys-message">No survey instances found</p>
      </div>
    );
  }

  return (
    <div className="recipient-breakdown">
      {surveyInstances.map(instance => (
        <div key={instance.id} className="survey-instance-breakdown">
          <div className="survey-instance-header">
            <h5 className="survey-type-title">{instance.survey_type} Survey</h5>
            <span className="survey-completion-badge">
              {instance.completed_responses}/{instance.expected_responses} completed 
              ({instance.completion_percentage}%)
            </span>
          </div>
          
          <div className="recipients-list">
            {/* Self Assessment */}
            <RecipientStatus 
              type="Self Assessment"
              completed={instance.self_completed}
              name={participantName}
              icon="👤"
            />
            
            {/* Manager */}
            {instance.manager && (
              <RecipientStatus 
                type="Manager"
                completed={instance.manager.completed}
                name={instance.manager.name}
                email={instance.manager.email}
                icon="👔"
              />
            )}
            
            {/* Direct Reports */}
            {instance.direct_reports && instance.direct_reports.length > 0 && (
              <>
                {instance.direct_reports.map(dr => (
                  <RecipientStatus 
                    key={dr.id}
                    type="Direct Report"
                    completed={dr.completed}
                    name={dr.name}
                    email={dr.email}
                    icon="👥"
                  />
                ))}
              </>
            )}
            
            {/* Adhoc Recipients */}
            {instance.adhoc_recipients && instance.adhoc_recipients.length > 0 && (
              <>
                {instance.adhoc_recipients.map(ar => (
                  <RecipientStatus 
                    key={ar.id}
                    type="Adhoc"
                    completed={ar.completed}
                    name={ar.name}
                    email={ar.email}
                    icon="💬"
                  />
                ))}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Recipient Status Component
 * Shows individual recipient completion status
 */
const RecipientStatus = ({ type, completed, name, email, icon }) => {
  return (
    <div className={`recipient-status ${completed ? 'completed' : 'pending'}`}>
      <div className="recipient-status-icon">
        {completed ? <CheckCircle size={16} /> : <XCircle size={16} />}
      </div>
      <div className="recipient-status-info">
        <span className="recipient-icon">{icon}</span>
        <span className="recipient-type">{type}:</span>
        <span className="recipient-name">{name}</span>
        {email && <span className="recipient-email">({email})</span>}
      </div>
      <div className={`recipient-status-label ${completed ? 'completed' : 'pending'}`}>
        {completed ? 'Completed' : 'Not Completed'}
      </div>
    </div>
  );
};

/**
 * Cohort Comparison View Component
 * Requirement: 10.9
 */
const CohortComparisonView = ({ filters }) => {
  const { getAccessToken } = useAuth();
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohorts, setSelectedCohorts] = useState([]);
  const [selectedCohortDetails, setSelectedCohortDetails] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comparisonMode, setComparisonMode] = useState('MANUAL'); // 'MANUAL' or 'PROGRAM_LEVEL'
  const [programLevelFilter, setProgramLevelFilter] = useState('ALL');

  useEffect(() => {
    loadCohorts();
  }, [filters]);

  useEffect(() => {
    if (selectedCohorts.length > 0) {
      loadComparisonData(selectedCohorts);
    } else {
      setComparisonData(null);
    }
  }, [selectedCohorts]);

  // Handle program level comparison mode
  useEffect(() => {
    if (comparisonMode === 'PROGRAM_LEVEL' && programLevelFilter !== 'ALL') {
      const cohortsInLevel = cohorts.filter(c => c.program_level === programLevelFilter);
      if (cohortsInLevel.length >= 2) {
        setSelectedCohorts(cohortsInLevel.map(c => c.id));
        setSelectedCohortDetails(cohortsInLevel);
      } else {
        setSelectedCohorts([]);
        setSelectedCohortDetails([]);
      }
    }
  }, [comparisonMode, programLevelFilter, cohorts]);

  const loadCohorts = async () => {
    setError(null);
    try {
      const data = await leadingAnalyticsAPI.getCohorts(getAccessToken, filters);
      const cohortsList = data.cohorts || [];
      setCohorts(cohortsList);
    } catch (error) {
      console.error('Error loading cohorts:', error);
      setError('Failed to load cohorts. Please try again.');
      setCohorts([]);
    }
  };

  const loadComparisonData = async (cohortIds) => {
    setLoading(true);
    setError(null);
    try {
      const data = await leadingAnalyticsAPI.getCohortComparison(getAccessToken, cohortIds);
      setComparisonData(data);
    } catch (error) {
      console.error('Error loading comparison data:', error);
      setError('Failed to load comparison data. Please try again.');
      setComparisonData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleComparisonModeChange = (mode) => {
    setComparisonMode(mode);
    setSelectedCohorts([]);
    setSelectedCohortDetails([]);
    setComparisonData(null);
    if (mode === 'MANUAL') {
      setProgramLevelFilter('ALL');
    }
  };

  const handleCohortSelect = (e) => {
    const cohortId = parseInt(e.target.value);
    if (cohortId && !selectedCohorts.includes(cohortId)) {
      const cohort = cohorts.find(c => c.id === cohortId);
      setSelectedCohorts(prev => [...prev, cohortId]);
      setSelectedCohortDetails(prev => [...prev, cohort]);
    }
    e.target.value = ''; // Reset dropdown
  };

  const handleRemoveCohort = (cohortId) => {
    setSelectedCohorts(prev => prev.filter(id => id !== cohortId));
    setSelectedCohortDetails(prev => prev.filter(c => c.id !== cohortId));
  };

  // Filter cohorts by program level
  const filteredCohorts = programLevelFilter === 'ALL' 
    ? cohorts 
    : cohorts.filter(c => c.program_level === programLevelFilter);

  // Get available cohorts (not already selected)
  const availableCohorts = filteredCohorts.filter(c => !selectedCohorts.includes(c.id));

  // Get unique program levels
  const programLevels = ['ALL', ...new Set(cohorts.map(c => c.program_level))];

  const formatProgramLevel = (level) => {
    if (level === 'ALL') return 'All Program Levels';
    return level.replace(/_/g, ' ');
  };

  return (
    <div className="cohort-comparison-view">
      {/* Comparison Mode Toggle */}
      <div className="comparison-mode-toggle">
        <button
          className={`mode-btn ${comparisonMode === 'MANUAL' ? 'active' : ''}`}
          onClick={() => handleComparisonModeChange('MANUAL')}
        >
          Manual Selection
        </button>
        <button
          className={`mode-btn ${comparisonMode === 'PROGRAM_LEVEL' ? 'active' : ''}`}
          onClick={() => handleComparisonModeChange('PROGRAM_LEVEL')}
        >
          Compare by Program Level
        </button>
      </div>

      {/* Filters and Selector */}
      <div className="comparison-controls">
        {comparisonMode === 'PROGRAM_LEVEL' ? (
          <div className="control-row">
            <div className="control-group">
              <label>Select Program Level to Compare All Cohorts</label>
              <select 
                value={programLevelFilter} 
                onChange={(e) => setProgramLevelFilter(e.target.value)}
                className="program-level-filter"
              >
                <option value="ALL">Select a program level...</option>
                {programLevels.filter(l => l !== 'ALL').map(level => (
                  <option key={level} value={level}>
                    {formatProgramLevel(level)} ({cohorts.filter(c => c.program_level === level).length} cohorts)
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="control-row">
            <div className="control-group">
              <label>Filter by Program Level (Optional)</label>
              <select 
                value={programLevelFilter} 
                onChange={(e) => setProgramLevelFilter(e.target.value)}
                className="program-level-filter"
              >
                {programLevels.map(level => (
                  <option key={level} value={level}>
                    {formatProgramLevel(level)}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="control-group">
              <label>Add Cohort to Compare</label>
              <select 
                onChange={handleCohortSelect}
                className="cohort-selector"
                disabled={availableCohorts.length === 0}
              >
                <option value="">
                  {availableCohorts.length === 0 ? 'No more cohorts available' : 'Select a cohort...'}
                </option>
                {availableCohorts.map(cohort => (
                  <option key={cohort.id} value={cohort.id}>
                    {cohort.name} ({cohort.participant_count} participants)
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Selected Cohorts Cards */}
      {selectedCohortDetails.length > 0 && (
        <div className="selected-cohorts-container">
          <h3>
            {comparisonMode === 'PROGRAM_LEVEL' 
              ? `Comparing ${formatProgramLevel(programLevelFilter)} Cohorts (${selectedCohortDetails.length})`
              : `Selected Cohorts (${selectedCohortDetails.length})`
            }
          </h3>
          <div className="selected-cohorts-grid">
            {selectedCohortDetails.map(cohort => (
              <div key={cohort.id} className="selected-cohort-card">
                {comparisonMode === 'MANUAL' && (
                  <button 
                    className="remove-cohort-btn"
                    onClick={() => handleRemoveCohort(cohort.id)}
                    title="Remove cohort"
                  >
                    ×
                  </button>
                )}
                <div className="cohort-card-header">
                  <h4>{cohort.name}</h4>
                  <span className="cohort-card-level">
                    {formatProgramLevel(cohort.program_level)}
                  </span>
                </div>
                <div className="cohort-card-stats">
                  <div className="stat-item">
                    <span className="stat-label">Participants:</span>
                    <span className="stat-value">{cohort.participant_count}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Status:</span>
                    <span className={`stat-value status-${cohort.status?.toLowerCase()}`}>
                      {cohort.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedCohorts.length < 2 && (
        <div className="comparison-empty-state">
          <p>
            {comparisonMode === 'PROGRAM_LEVEL' 
              ? 'Select a program level to compare all cohorts in that level'
              : 'Select at least 2 cohorts to compare their performance'
            }
          </p>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading comparison data...</p>
        </div>
      ) : comparisonData && selectedCohorts.length >= 2 ? (
        <div className="comparison-content">
          {/* Comparison Table */}
          <ComparisonTable data={comparisonData.cohorts} />
          
          {/* Comparison Charts */}
          <div className="comparison-charts-grid">
            <ComparisonChart
              title="Completion Rates"
              data={comparisonData.cohorts}
              metric="completion_rate"
              suffix="%"
            />
            <ComparisonChart
              title="Average Improvement"
              data={comparisonData.cohorts}
              metric="average_improvement"
              suffix="%"
            />
            <ComparisonChart
              title="Success Rates"
              data={comparisonData.cohorts}
              metric="success_rate"
              suffix="%"
            />
            <ComparisonChart
              title="Participants Meeting Goal"
              data={comparisonData.cohorts}
              metric="participants_meeting_goal"
              suffix=""
            />
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <Target size={48} />
          <p>Select at least 2 cohorts to compare</p>
        </div>
      )}
    </div>
  );
};

/**
 * Comparison Table Component
 */
const ComparisonTable = ({ data }) => {
  return (
    <div className="leading-card comparison-table-card">
      <div className="card-header">
        <h3 className="card-title">Side-by-Side Comparison</h3>
      </div>
      <div className="card-content">
        <table className="comparison-table">
          <thead>
            <tr>
              <th>Cohort</th>
              <th>Program Level</th>
              <th>Participants</th>
              <th>Completion Rate</th>
              <th>Avg Improvement</th>
              <th>Meeting Goal</th>
              <th>Success Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.map(cohort => (
              <tr key={cohort.cohort_id}>
                <td className="comparison-cohort-name">{cohort.cohort_name}</td>
                <td>{cohort.program_level.replace(/_/g, ' ')}</td>
                <td>{cohort.participant_count}</td>
                <td>
                  <span className={`comparison-metric-value ${cohort.completion_rate >= 80 ? 'positive' : 'neutral'}`}>
                    {cohort.completion_rate !== null ? cohort.completion_rate.toFixed(1) : '0'}%
                  </span>
                </td>
                <td>
                  <span className={`comparison-metric-value ${cohort.average_improvement >= 10 ? 'positive' : 'neutral'}`}>
                    {cohort.average_improvement !== null ? cohort.average_improvement.toFixed(1) : '0'}%
                  </span>
                </td>
                <td>
                  {cohort.participants_meeting_goal} / {cohort.participant_count}
                </td>
                <td>
                  <span className={`comparison-metric-value ${cohort.success_rate >= 80 ? 'positive' : 'neutral'}`}>
                    {cohort.success_rate !== null ? cohort.success_rate.toFixed(1) : '0'}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Comparison Chart Component
 */
const ComparisonChart = ({ title, data, metric, suffix }) => {
  const maxValue = Math.max(...data.map(d => d[metric]));
  
  // Generate colors for each cohort
  const colors = [
    '#3b82f6', // Blue
    '#10b981', // Green
    '#f59e0b', // Amber
    '#8b5cf6', // Purple
    '#ef4444'  // Red
  ];

  return (
    <div className="leading-card comparison-chart-card">
      <div className="card-header">
        <h3 className="card-title">{title}</h3>
      </div>
      <div className="card-content">
        <div className="comparison-bar-chart">
          {data.map((cohort, index) => (
            <div key={cohort.cohort_id} className="comparison-bar-row">
              <div className="comparison-bar-label" title={cohort.cohort_name}>
                {cohort.cohort_name}
              </div>
              <div className="comparison-bar-container">
                <div
                  className="comparison-bar-fill"
                  style={{
                    width: `${(cohort[metric] / maxValue) * 100}%`,
                    background: colors[index % colors.length]
                  }}
                >
                  <span className="comparison-bar-value">
                    {cohort[metric] !== null && cohort[metric] !== undefined ? cohort[metric].toFixed(1) : '0'}{suffix}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Overall Progress View Component
 * Shows program-wide performance across all participants
 */
const OverallProgressView = ({ filters }) => {
  const { getAccessToken } = useAuth();
  const [overallData, setOverallData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCompetency, setExpandedCompetency] = useState(null);

  useEffect(() => {
    loadOverallAnalytics();
  }, [filters]);

  const loadOverallAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leadingAnalyticsAPI.getOverallAnalytics(getAccessToken, filters);
      setOverallData(data);
    } catch (error) {
      console.error('Error loading overall analytics:', error);
      setError('Failed to load overall analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="leading-loading-state">
        <div className="leading-spinner"></div>
        <p>Loading overall analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leading-error-state">
        <XCircle size={48} />
        <p>{error}</p>
        <button onClick={loadOverallAnalytics} className="leading-btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (!overallData) return null;

  return (
    <div className="overall-progress-view">
      {/* Key Metrics Grid */}
      <div className="metrics-grid">
        <MetricCard 
          title="Total Participants" 
          value={overallData.statistics.total_participants}
          icon={Users}
          color="#3b82f6"
        />
        <MetricCard 
          title="Completion Rate" 
          value={`${overallData.statistics.completion_rate.toFixed(1)}%`}
          icon={CheckCircle}
          color="#10b981"
        />
        <MetricCard 
          title="Average Improvement" 
          value={overallData.statistics.average_improvement !== null 
            ? `${overallData.statistics.average_improvement.toFixed(1)}%` 
            : 'N/A'}
          icon={TrendingUp}
          color="#8b5cf6"
        />
        <MetricCard 
          title="Success Rate" 
          value={`${overallData.statistics.success_rate.toFixed(1)}%`}
          subtitle={`${overallData.statistics.participants_meeting_goal} meeting 10% goal`}
          icon={Award}
          color="#f59e0b"
        />
      </div>

      {/* Program Level Breakdown */}
      <ProgramLevelBreakdown data={overallData.by_program_level} />

      {/* Overall Competency Performance */}
      <div className="leading-section">
        <h3 className="leading-section-title">Overall Competency Performance</h3>
        <CompetencyAveragesChart 
          competencyAverages={Object.values(overallData.competency_averages)}
          expandedCompetency={expandedCompetency}
          setExpandedCompetency={setExpandedCompetency}
        />
      </div>

      {/* Competency Heatmap */}
      <div className="leading-section">
        <h3 className="leading-section-title">Competency Heatmap</h3>
        <CompetencyHeatmap heatmapData={overallData.competency_heatmap} />
      </div>

      {/* Survey Completion Stats */}
      <SurveyCompletionStats data={overallData.survey_completion} />
    </div>
  );
};

/**
 * Program Level Breakdown Component
 */
const ProgramLevelBreakdown = ({ data }) => {
  const [sortBy, setSortBy] = useState('program_level');
  const [sortOrder, setSortOrder] = useState('asc');

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="leading-section">
        <h3 className="leading-section-title">Program Level Breakdown</h3>
        <p className="leading-empty-state">No program level data available</p>
      </div>
    );
  }

  const programLevels = Object.values(data);

  const sortedLevels = [...programLevels].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    if (sortBy === 'program_level') {
      // Sort by program level hierarchy
      const order = ['VISIONARY_LEADERS', 'LEADERS_OF_LEADERS', 'EXPERT_LEADERS', 'FRONTLINE_LEADERS', 'INFORMAL_LEADERS'];
      aVal = order.indexOf(a.program_level);
      bVal = order.indexOf(b.program_level);
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const formatProgramLevel = (level) => {
    return level.split('_').map(word => 
      word.charAt(0) + word.slice(1).toLowerCase()
    ).join(' ');
  };

  return (
    <div className="leading-section">
      <h3 className="leading-section-title">Program Level Breakdown</h3>
      <div className="program-level-table-container">
        <table className="program-level-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('program_level')} className="sortable">
                Program Level {sortBy === 'program_level' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('participant_count')} className="sortable">
                Participants {sortBy === 'participant_count' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('completion_rate')} className="sortable">
                Completion Rate {sortBy === 'completion_rate' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('average_improvement')} className="sortable">
                Avg Improvement {sortBy === 'average_improvement' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('success_rate')} className="sortable">
                Success Rate {sortBy === 'success_rate' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedLevels.map((level) => (
              <tr key={level.program_level}>
                <td className="program-level-name">{formatProgramLevel(level.program_level)}</td>
                <td>{level.participant_count}</td>
                <td>
                  <span className={`completion-badge ${level.completion_rate >= 80 ? 'high' : level.completion_rate >= 50 ? 'medium' : 'low'}`}>
                    {level.completion_rate.toFixed(1)}%
                  </span>
                </td>
                <td>
                  {level.average_improvement !== null ? (
                    <span className={`improvement-badge ${level.average_improvement >= 10 ? 'positive' : 'neutral'}`}>
                      {level.average_improvement.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="improvement-badge neutral">N/A</span>
                  )}
                </td>
                <td>
                  <span className={`success-badge ${level.success_rate >= 75 ? 'high' : level.success_rate >= 50 ? 'medium' : 'low'}`}>
                    {level.success_rate.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Survey Completion Stats Component
 */
const SurveyCompletionStats = ({ data }) => {
  if (!data) return null;

  const surveys = [
    { type: 'Baseline', completed: data.baseline_completed, total: data.baseline_total, rate: data.baseline_rate, color: '#3b82f6' },
    { type: 'Midpoint', completed: data.midpoint_completed, total: data.midpoint_total, rate: data.midpoint_rate, color: '#8b5cf6' },
    { type: 'Final', completed: data.final_completed, total: data.final_total, rate: data.final_rate, color: '#10b981' }
  ];

  return (
    <div className="leading-section">
      <h3 className="leading-section-title">Survey Completion Status</h3>
      <div className="survey-completion-grid">
        {surveys.map((survey) => (
          <div key={survey.type} className="survey-completion-card">
            <div className="survey-completion-header">
              <span className="survey-completion-type">{survey.type}</span>
              <span className="survey-completion-count">
                {survey.completed} / {survey.total}
              </span>
            </div>
            <div className="survey-completion-bar-container">
              <div 
                className="survey-completion-bar" 
                style={{ 
                  width: `${survey.rate}%`,
                  backgroundColor: survey.color
                }}
              />
            </div>
            <div className="survey-completion-rate">{survey.rate.toFixed(1)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LeadingAnalyticsDashboard;
